import React, { useCallback, useState, useEffect } from 'react';
import { format, parseISO, subDays, startOfWeek, addDays } from 'date-fns';
import { API, ShareAPI } from '../../API';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ampli } from '../../ampli';
import { StoryContent } from '../../components/StoryContent/StoryContent';
import { Loader } from '../../components/Loader/Loader';
import { useCurrentUser } from '../../contexts/CurrentUser';
import styles from './TopicReportPageNew.module.scss';
import {toast} from "react-toastify";

export const EventPage = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { topicReportId, eventId } = useParams();

  const [report, setReport] = useState(null);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [isShare, setIsShare] = useState(false);
  const [isData, setIsData] = useState(false);
  const [sourceGroups, setSourceGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryGroups, setCountryGroups] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sorting, setSorting] = useState({
    fieldName: 'rating',
    isAscending: false,
  });
  const [updatedDateTime, setUpdatedDateTime] = useState(null);
  const scrollRef = React.createRef();
  const caseScrollRef = React.createRef();

  const fetchReport = useCallback(() => {
    let id = topicReportId;
    API.fetch('GET', `/API/v1/topic-modeling/${id}`)
      .then((data) => {
        setReport(data);
        let currentDate = new Date();

        if (data.dates_preset) {
          let preset = data.dates_preset;

          if (preset === 'FIXED') {
            setStartDate(new Date(data.preset_start_date));
            setEndDate(new Date(data.preset_end_date));
          } else if (preset === 'LAST_7_DAYS') {
            setStartDate(subDays(currentDate, 7));
            setEndDate(currentDate);
          } else if (preset === 'YESTERDAY') {
            setStartDate(subDays(currentDate, 1));
            setEndDate(subDays(currentDate, 1));
          } else if (preset === 'YESTERDAY_TODAY') {
            setStartDate(subDays(currentDate, 1));
            setEndDate(currentDate);
          } else if (preset === 'CURRENT_WEEK') {
            setStartDate(addDays(startOfWeek(currentDate), 1));
            setEndDate(currentDate);
          } else if (preset === 'LAST_WEEK') {
            setStartDate(addDays(startOfWeek(subDays(currentDate, 7)), 1));
            setEndDate(startOfWeek(subDays(currentDate, 1)));
          }
        } else {
          setStartDate(new Date(data.start_date));
          setEndDate(new Date(data.end_date ?? currentDate));
        }

        if (data.preset_sort) {
          setSorting({
            fieldName: data.preset_sort.replace('-', ''),
            isAscending: !data.preset_sort.startsWith('-'),
          });
        }

        if (data.last_updated_at) {
          setUpdatedDateTime(
            format(parseISO(data.last_updated_at + 'Z'), 'yyyy-LL-dd HH:mm'),
          );
        }

        if (data?.widget_config?.event_card === 'messages') {
          setSorting({ fieldName: 'messages_count', isAscending: false });
        }
      })
      .catch((error) => {
        setReport(null);
        if (error.message === 'Client error: Narrative not found') {
          setError(t('Narrative not found'));
        } else {
          setError(t('Failed to fetch narrative'));
        }
      });
  }, [topicReportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const fetchEvent = () => {
    API
      .fetch('GET', `/API/v1/topic-modeling/${topicReportId}/events/${eventId}`)
      .then((data) => {
        setEvent(data);
        setIsData(true);
      }).catch((error) => {
        if (error.message === 'Client error: Event not found') {
          toast.error(t('Event not found'));
        } else {
          toast.error(t('Failed to fetch'));
        }
        setEvent(null);
        setIsData(false);
      });
  };

  useEffect(() => {
    fetchEvent();
  }, [topicReportId, eventId]);


  useEffect(() => {
    ampli.track({
      event_type: 'Open TM Event Page',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        topic_report_id: topicReportId,
        event_id: eventId,
      },
    });
  }, []);

  if(!report && error) {
    return <div className='noData'>{error}</div>
  }

  if (!report) {
    return <Loader />;
  }

  return (
    <div className={styles.root}>
      <div className="page-header">
        <div className="breadcrumb">
          <div className="breadcrumb">
            <span>
              <Link to={`/topic_reports/${topicReportId}`}>
                {report?.name}
              </Link>
            </span>
            <span>{t('Event')}</span>
          </div>
        </div>
      </div>
      <div className={styles.reportStory}>
        <StoryContent
          report={report}
          story={event}
          isShare={isShare}
          isData={isData}
          sourceGroups={sourceGroups}
          countries={countries}
          countryGroups={countryGroups}
          sentiment={sentiment}
          startDate={startDate}
          endDate={endDate}
          scrollRef={topicReportId ? scrollRef : caseScrollRef}
          isAttitude={report.id == 58692 ? true : false}
        />
      </div>
    </div>
  );
};
